import React from "react";
import { BookingLanguage, BookingStep, TimeSlot } from "@/types/booking";
import type { SlotSelectionUpdateOptions } from "@/hooks/booking/useTimeSlots";
import { Product } from "@/components/product-step/types";
import { FormValues } from "@/components/booking-form/types";
import ProductStep from "@/components/ProductStep";
import AddOnsStep from "@/components/AddOnsStep";
import ClinicStep from "@/components/ClinicStep";
import ConfirmationStep from "@/components/ConfirmationStep";
import ContentLayout from "./ContentLayout";
import { scrollToTopDelayed } from "@/utils/scrollUtils";
import { MembershipPlanStep } from "@/components/membership-step/MembershipPlanStep";

// Define the component map type for consistency
type ComponentMap =
  | Record<string, string[]>
  | { internalUpdate: boolean }
  | { skipAddons: boolean };

interface StepRendererProps {
  currentStep: BookingStep;
  handleProductSelect: (
    productIds: string[],
    componentMap?: ComponentMap,
  ) => void;
  handleClinicSelect: () => void;
  selectedProducts: string[];
  selectedClinic: string;
  handleClinicChange: (clinicId: string) => void;
  handleProductRemove: (productId: string) => void;
  handleBack: () => void;
  canGoBack: boolean;
  language: BookingLanguage;
  currentDate?: Date;
  handleDateSelect: (date: Date | undefined) => void;
  selectedTimeSlots: TimeSlot[];
  bookingType: "request";
  setBookingType: (type: "request") => void;
  handleTimeSelect: (
    times: string[],
    options?: SlotSelectionUpdateOptions,
  ) => void;
  handleDeleteTimeSlot: (slot: TimeSlot) => void;
  setCurrentStep: (step: BookingStep) => void;
  handleFormSubmit: (data: FormValues) => void;
  formData: Partial<FormValues>;
  discount: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
  bookingId: string;
  setBookingId: (id: string) => void;
  selectedProductDetails: Product[];
  priceSummaryTranslations: { selectedProducts: string; totalPrice: string };
  packageComponentMap?: Record<string, string[]>;
  selectedAddOns?: string[];
  onAddOnToggle?: (productId: string) => void;
  allProducts?: Product[];
  viewMode: "packages" | "configure";
  setViewMode: (mode: "packages" | "configure") => void;
  selectedPackageType?: string; // Filter add-ons by package type
}

const StepRenderer: React.FC<StepRendererProps> = ({
  currentStep,
  handleProductSelect,
  handleClinicSelect,
  selectedProducts,
  selectedClinic,
  handleClinicChange,
  handleProductRemove,
  handleBack,
  canGoBack,
  language,
  currentDate,
  handleDateSelect,
  selectedTimeSlots,
  bookingType,
  setBookingType,
  handleTimeSelect,
  handleDeleteTimeSlot,
  setCurrentStep,
  handleFormSubmit,
  formData,
  discount,
  bookingId,
  setBookingId,
  selectedProductDetails,
  priceSummaryTranslations,
  packageComponentMap = {},
  selectedAddOns = [],
  onAddOnToggle,
  selectedPackageType,
  allProducts = [],
  viewMode,
  setViewMode,
}) => {
  if (currentStep === "product") {
    return <ProductStep />;
  }

  // Membership cadence — a real, dedicated step. Reuses the add-ons step chrome
  // (real PersistentDesktopSidebar + ExpandableOrderSummary), swapping the
  // add-ons grid for the cadence component. Navigation is the real state machine
  // (goToStep "cadence" in / "addons" out, goBack → product). The membership
  // path continues to the same add-ons step as standard booking.
  if (currentStep === "cadence") {
    const selectedPackage = selectedProductDetails.find(
      (p) => p.product_type === "package",
    );

    // Baseline test not selected yet (e.g. a direct refresh on the cadence URL)
    // — fall back to the product step rather than rendering empty chrome.
    if (!selectedPackage || !onAddOnToggle) {
      setCurrentStep("product");
      scrollToTopDelayed(40);
      return null;
    }

    return (
      <AddOnsStep
        onContinue={() => {
          setCurrentStep("addons");
          scrollToTopDelayed(150);
        }}
        onBack={handleBack}
        language={language}
        packageProduct={selectedPackage}
        allProducts={allProducts}
        selectedAddOns={selectedAddOns}
        onAddOnToggle={onAddOnToggle}
        setCurrentStep={setCurrentStep}
        selectedPackageType={selectedPackageType}
        selectedClinic={selectedClinic}
        selectedTimeSlots={selectedTimeSlots}
        globalSelectedProducts={selectedProducts}
        contentOverride={<MembershipPlanStep />}
      />
    );
  }

  if (currentStep === "addons") {
    // Find the selected package
    const selectedPackage = selectedProductDetails.find(
      (p) => p.product_type === "package",
    );

    if (!selectedPackage || !onAddOnToggle) {
      // If no products selected at all, go back to product step to avoid infinite loop
      // (clinic step also requires products, so navigating there would redirect back here)
      if (selectedProducts.length === 0) {
        setCurrentStep("product");
        scrollToTopDelayed(40);
        return null;
      }
      // If products are selected but product details haven't loaded yet (e.g. after
      // page refresh), wait for them instead of incorrectly redirecting to clinic
      if (selectedProductDetails.length === 0) {
        return null;
      }
      // If products exist but no package (configure mode), skip to clinic step
      setCurrentStep("clinic");
      scrollToTopDelayed(40);
      return null;
    }

    return (
      <AddOnsStep
        onContinue={() => {
          // Use setCurrentStep with skipValidation flag
          // Since we're coming from addons, we want to continue to clinic without re-validating addons
          setCurrentStep("clinic");
          // Scroll to top on navigation to clinic step
          scrollToTopDelayed(150);
        }}
        onBack={handleBack}
        language={language}
        packageProduct={selectedPackage}
        allProducts={allProducts}
        selectedAddOns={selectedAddOns}
        onAddOnToggle={onAddOnToggle}
        setCurrentStep={setCurrentStep}
        selectedPackageType={selectedPackageType}
        selectedClinic={selectedClinic}
        selectedTimeSlots={selectedTimeSlots}
        globalSelectedProducts={selectedProducts}
      />
    );
  }

  if (currentStep === "clinic") {
    return <ClinicStep />;
  }

  if (currentStep === "confirmation") {
    return (
      <ConfirmationStep
        timeSlots={selectedTimeSlots}
        bookingType={bookingType}
        language={language}
        selectedProducts={selectedProducts}
        selectedAddOns={selectedAddOns} // Pass selectedAddOns to ConfirmationStep
        setBookingId={setBookingId}
        formData={formData}
        packageComponentMap={packageComponentMap}
      />
    );
  }

  if (currentStep === "date" || currentStep === "details") {
    return (
      <ContentLayout
        currentStep={currentStep}
        currentDate={currentDate}
        handleDateSelect={handleDateSelect}
        selectedTimeSlots={selectedTimeSlots}
        language={language}
        selectedClinic={selectedClinic}
        handleTimeSelect={handleTimeSelect}
        handleDeleteTimeSlot={handleDeleteTimeSlot}
        setCurrentStep={setCurrentStep}
        bookingType={bookingType}
        setBookingType={setBookingType}
        handleFormSubmit={handleFormSubmit}
        selectedProductDetails={selectedProductDetails}
        discount={discount}
        bookingId={bookingId}
        selectedProducts={selectedProducts}
        selectedAddOns={selectedAddOns} // Pass selectedAddOns to ContentLayout
        t={priceSummaryTranslations}
        packageComponentMap={packageComponentMap}
        handleBack={handleBack}
        canGoBack={canGoBack}
        handleProductRemove={handleProductRemove}
        onAddOnToggle={onAddOnToggle}
      />
    );
  }

  return null;
};

export default StepRenderer;
